<template>
  <div class="idea-generator">
    <template v-if="invalidState || loading">
      <div class="loading loading--error" v-if="invalidState">
        <p><img src="../assets/angry-sun.png" alt=""></p>
        <p>Whoops! That idea generator code is not valid.<br>Try generating your own!</p>
        <p><router-link to="/idea-generator" class="idea-generator__button"><i class="material-icons">shuffle</i></router-link></p>
      </div>
      <template v-if="loading">
        <div class="loading">
          <p><img src="../assets/spinning-shell.gif" alt=""></p>
          <p>Loading...</p>
        </div>
      </template>
    </template>
    <template v-else>
      <h1 class="idea-generator__title">
        <span class="idea-generator__title-text">Make a Mario Maker 2 level...</span>
        <button class="idea-generator__button" :class="{'idea-generator__button--active': settingsOpen }"><i class="material-icons" @click="toggleSettings()">settings</i></button>
        <button class="idea-generator__button"><i class="material-icons" @click="shuffle('all')">shuffle</i></button>
      </h1>
      <aside class="idea-generator-settings" v-if="settingsOpen">
        <div class="idea-generator-settings__block">
          <h2 class="idea-generator-settings__title">Select games to choose from&nbsp;-&nbsp;<span class="idea-generator-settings__title-action" @click="toggleAllBlacklist('games')">toggle all</span></h2>
          <p class="idea-generator-settings__elements">
            <span
              v-for="game in games"
              :key="game.id"
              class="game"
              @click="toggleBlacklist(game.id, 'Games')"
              :class="{[`game--${game.slug}`]: true, 'blacklisted': isBlacklisted(game.id, 'Games')}"
              v-tooltip="game.name"
            />
          </p>
        </div>
        <div class="idea-generator-settings__block-container">
          <div class="idea-generator-settings__block">
            <h2 class="idea-generator-settings__title">Select themes to choose from&nbsp;-&nbsp;<span class="idea-generator-settings__title-action" @click="toggleAllBlacklist('themes')">toggle all</span></h2>
            <ul class="idea-generator-settings__elements">
              <li
                class="idea-generator-settings__element"
                v-for="theme in themes"
                :key="theme.id"
                v-tooltip="theme.name"
                @click="toggleBlacklist(theme.id, 'Themes')"
                :class="{'blacklisted': isBlacklisted(theme.id, 'Themes')}"
              >
                <img :src="require(`../assets/theme_${theme.slug}.png`)">
              </li>
            </ul>
          </div>
          <div class="idea-generator-settings__block">
            <label class="idea-generator-settings__label" for="blocks-amount">
              How many featured blocks?
            </label>
            <input type="text" id="blocks-amount" placeholder="2" v-model="featuredAmount" class="idea-generator-settings__input">
          </div>
        </div>
        <div class="idea-generator-settings__block idea-generator-settings__block--with-child">
          <div class="idea-generator-settings__main-selection">
            <h2 class="idea-generator-settings__title">Select blocks to choose from&nbsp;-&nbsp;<span class="idea-generator-settings__title-action" @click="toggleAllBlacklist('block')">toggle all</span></h2>
            <ul class="idea-generator-settings__elements">
              <li
                class="idea-generator-settings__element"
                v-for="element in blocks"
                :key="element.id"
                v-tooltip="element.name"
                @click="toggleBlacklist(element.id, 'Elements')"
                :class="{'blacklisted': isBlacklisted(element.id, 'Elements')}"
              >
                <img :src="`${publicPath}generator/${element.type}/${element.slug}_${(!element[selectedGame.slug] || element.noImage) ? element.fallback : selectedGame.slug }_icon.png`">
              </li>
            </ul>
          </div>
          <div class="idea-generator-settings__amount">
            <label class="idea-generator-settings__label" for="blocks-amount">
              How many?
            </label>
            <input type="text" id="blocks-amount" placeholder="2" v-model="blockAmount" class="idea-generator-settings__input">
          </div>
        </div>
        <div class="idea-generator-settings__block idea-generator-settings__block--with-child">
          <div class="idea-generator-settings__main-selection">
            <h2 class="idea-generator-settings__title">Select items to choose from&nbsp;-&nbsp;<span class="idea-generator-settings__title-action" @click="toggleAllBlacklist('item')">toggle all</span></h2>
            <ul class="idea-generator-settings__elements">
              <li
                class="idea-generator-settings__element"
                v-for="element in items"
                :key="element.id"
                v-tooltip="element.name"
                @click="toggleBlacklist(element.id, 'Elements')"
                :class="{'blacklisted': isBlacklisted(element.id, 'Elements')}"
              >
                <img :src="`${publicPath}generator/${element.type}/${element.slug}_${(!element[selectedGame.slug] || element.noImage) ? element.fallback : selectedGame.slug }_icon.png`">
              </li>
            </ul>
          </div>
          <div class="idea-generator-settings__amount">
            <label class="idea-generator-settings__label" for="items-amount">
              How many?
            </label>
            <input type="text" id="items-amount" placeholder="2" v-model="itemAmount" class="idea-generator-settings__input">
          </div>
        </div>
        <div class="idea-generator-settings__block idea-generator-settings__block--with-child">
          <div class="idea-generator-settings__main-selection">
            <h2 class="idea-generator-settings__title">Select gizmos to choose from&nbsp;-&nbsp;<span class="idea-generator-settings__title-action" @click="toggleAllBlacklist('gizmo')">toggle all</span></h2>
            <ul class="idea-generator-settings__elements">
              <li
                class="idea-generator-settings__element"
                v-for="element in gizmos"
                :key="element.id"
                v-tooltip="element.name"
                @click="toggleBlacklist(element.id, 'Elements')"
                :class="{'blacklisted': isBlacklisted(element.id, 'Elements')}"
              >
                <img :src="`${publicPath}generator/${element.type}/${element.slug}_${(!element[selectedGame.slug] || element.noImage) ? element.fallback : selectedGame.slug }_icon.png`">
              </li>
            </ul>
          </div>
          <div class="idea-generator-settings__amount">
            <label class="idea-generator-settings__label" for="gizmos-amount">
              How many?
            </label>
            <input type="text" id="gizmos-amount" placeholder="2" v-model="gizmoAmount" class="idea-generator-settings__input">
          </div>
        </div>
        <div class="idea-generator-settings__block idea-generator-settings__block--with-child">
          <div class="idea-generator-settings__main-selection">
            <h2 class="idea-generator-settings__title">Select enemies to choose from&nbsp;-&nbsp;<span class="idea-generator-settings__title-action" @click="toggleAllBlacklist('enemy')">toggle all</span></h2>
            <ul class="idea-generator-settings__elements">
              <li
                class="idea-generator-settings__element"
                v-for="element in enemies"
                :key="element.id"
                v-tooltip="element.name"
                @click="toggleBlacklist(element.id, 'Elements')"
                :class="{'blacklisted': isBlacklisted(element.id, 'Elements')}"
              >
                <img :src="`${publicPath}generator/${element.type}/${element.slug}_${(!element[selectedGame.slug] || element.noImage) ? element.fallback : selectedGame.slug }_icon.png`">
              </li>
            </ul>
          </div>
          <div class="idea-generator-settings__amount">
            <label class="idea-generator-settings__label" for="enemies-amount">
              How many?
            </label>
            <input type="text" id="enemies-amount" placeholder="2" v-model="enemyAmount" class="idea-generator-settings__input">
          </div>
        </div>
      </aside>
      <template v-if="!settingsOpen">
        <div class="feature-group">
          <h2 class="feature-group__title">With these settings</h2>
          <div class="feature">
            <h2 class="feature__title">
              Game Theme
              <button class="feature__button"><i class="material-icons" @click="shuffle('game')">shuffle</i></button>
            </h2>
            <ul class="feature__list">
              <li
                v-tooltip="selectedGame.name"
                v-if="selectedGame"
                class="feature__item feature__item--extra feature__item--game"
              >
                <span
                  class="game"
                  :class="`game--${selectedGame.slug}`"
                />
              </li>
            </ul>
          </div>
          <div class="feature">
            <h2 class="feature__title">
              Level Theme
              <button class="feature__button"><i class="material-icons" @click="shuffle('theme')">shuffle</i></button>
            </h2>
            <ul class="feature__list">
              <li class="feature__item feature__item--extra" v-tooltip="selectedTheme.name">
                <img :src="require(`../assets/theme_${selectedTheme.slug}.png`)">
              </li>
            </ul>
          </div>
          <div class="feature">
            <h2 class="feature__title">
              Featured Blocks
              <button class="feature__button"><i class="material-icons" @click="shuffle('featured')">shuffle</i></button>
            </h2>
            <ul class="feature__list">
              <li
                class="feature__item"
                v-for="element in featuredElements"
                :key="element.id"
                :class="`feature__item--${element.type}`"
                v-tooltip="element.name"
              >
                <img :src="`${publicPath}generator/${element.type}/${element.slug}_${(!element[selectedGame.slug] || element.noImage) ? element.fallback : selectedGame.slug }_icon.png`">
                <span class="feature__name">{{element.name}}</span>
              </li>
            </ul>
          </div>
        </div>
        <div class="feature-group">
          <h2 class="feature-group__title">With only these elements</h2>
          <div class="feature">
            <h2 class="feature__title">
              Blocks
              <button class="feature__button"><i class="material-icons" @click="shuffle('block')">shuffle</i></button>
            </h2>
            <ul class="feature__list">
              <li
                class="feature__item"
                v-for="element in selectedBlocks"
                :key="element.id"
                :class="`feature__item--${element.type}`"
                v-tooltip="element.name"
              >
                <img :src="`${publicPath}generator/${element.type}/${element.slug}_${(!element[selectedGame.slug] || element.noImage) ? element.fallback : selectedGame.slug }_icon.png`">
                <span class="feature__name">{{element.name}}</span>
              </li>
            </ul>
          </div>

          <div class="feature">
            <h2 class="feature__title">
              Items
              <button class="feature__button"><i class="material-icons" @click="shuffle('item')">shuffle</i></button>
            </h2>
            <ul class="feature__list">
              <li
                class="feature__item"
                v-for="element in selectedItems"
                :key="element.id"
                :class="`feature__item--${element.type}`"
                v-tooltip="element.name"
              >
                <img :src="`${publicPath}generator/${element.type}/${element.slug}_${(!element[selectedGame.slug] || element.noImage) ? element.fallback : selectedGame.slug }_icon.png`">
                <span class="feature__name">{{element.name}}</span>
              </li>
            </ul>
          </div>
          <div class="feature">
            <h2 class="feature__title">
              Gizmos
              <button class="feature__button"><i class="material-icons" @click="shuffle('gizmo')">shuffle</i></button>
            </h2>
            <ul class="feature__list">
              <li
                class="feature__item"
                v-for="element in selectedGizmos"
                :key="element.id"
                :class="`feature__item--${element.type}`"
                v-tooltip="element.name"
              >
                <img :src="`${publicPath}generator/${element.type}/${element.slug}_${(!element[selectedGame.slug] || element.noImage) ? element.fallback : selectedGame.slug }_icon.png`">
                <span class="feature__name">{{element.name}}</span>
              </li>
            </ul>
          </div>
          <div class="feature">
            <h2 class="feature__title">
              Enemies
              <button class="feature__button"><i class="material-icons" @click="shuffle('enemy')">shuffle</i></button>
            </h2>
            <ul class="feature__list">
              <li
                class="feature__item"
                v-for="element in selectedEnemies"
                :key="element.id"
                :class="`feature__item--${element.type}`"
                v-tooltip="element.name"
              >
                <img :src="`${publicPath}generator/${element.type}/${element.slug}_${(!element[selectedGame.slug] || element.noImage) ? element.fallback : selectedGame.slug }_icon.png`">
                <span class="feature__name">{{element.name}}</span>
              </li>
            </ul>
          </div>
        </div>
        <div class="idea-generator-sharing">
          <label for="url">Share your results and settings by sharing this URL:</label>
          <input type="text" id="url" @focus="$event.target.select()" class="idea-generator-sharing__url" :value="`https://randomactsofmario.com/idea-generator/${urlEncodedHash}`">
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { compressToEncodedURIComponent } from 'lz-string';

import parseSharingCode from '../utils/parse-sharing-code';

export default {
  metaInfo() {
    let description = 'Challenge yourself and try making a level with only a couple of blocks!';
    if (this.fromUrl && this.featuredElements.length > 1) {
      const blocks = `${getArticle(this.featuredElements[0].name)} ${this.featuredElements[0].name} and ${getArticle(this.featuredElements[1].name)} ${this.featuredElements[1].name}`;
      description = `Challenge yourself and try making a ${this.selectedGame.name} level featuring ${blocks}!`;
    }

    const meta = [
      { name: 'twitter-card', content: 'summary' },
      { name: 'description', content: description },
      { property: 'og:title', content: 'Level Ideas Generator for Mario Maker 2 – Random Acts of Mario' },
      { property: 'og:description', content: description },
      { property: 'og:image', content: `${process.env.VUE_APP_URL}twitter-small-card.png` },
    ];

    if (this.invalidState) {
      meta.push({ name: 'prerender-status-code', content: '404' });
    }

    return {
      title: 'Level Ideas Generator for Mario Maker 2',
      meta,
    };
  },
  computed: {
    ...mapGetters([
      'selectedBlocks',
      'selectedGame',
      'selectedTheme',
      'selectedEnemies',
      'selectedGizmos',
      'selectedItems',
      'featuredElements',
      'blocks',
      'items',
      'gizmos',
      'enemies',
      'generatorHash',
    ]),
    ...mapState({
      loading: state => state.generator.loading,
      games: state => state.generator.games,
      themes: state => state.generator.themes,
      blacklistedGames: state => state.generator.blacklistedGames,
      blacklistedThemes: state => state.generator.blacklistedThemes,
      blacklistedElements: state => state.generator.blacklistedElements,
      invalidState: state => state.generator.invalidState,
    }),
    urlEncodedHash() {
      return compressToEncodedURIComponent(this.generatorHash);
    },
    blockAmount: {
      set(amount) {
        this.$store.commit('setAmount', { type: 'block', amount });
      },
      get() {
        return this.$store.state.generator.blockAmount;
      },
    },
    itemAmount: {
      set(amount) {
        this.$store.commit('setAmount', { type: 'item', amount });
      },
      get() {
        return this.$store.state.generator.itemAmount;
      },
    },
    gizmoAmount: {
      set(amount) {
        this.$store.commit('setAmount', { type: 'gizmo', amount });
      },
      get() {
        return this.$store.state.generator.gizmoAmount;
      },
    },
    enemyAmount: {
      set(amount) {
        this.$store.commit('setAmount', { type: 'enemy', amount });
      },
      get() {
        return this.$store.state.generator.enemyAmount;
      },
    },
    featuredAmount: {
      set(amount) {
        this.$store.commit('setAmount', { type: 'featured', amount });
      },
      get() {
        return this.$store.state.generator.featuredAmount;
      },
    },
  },
  data() {
    return {
      publicPath: process.env.BASE_URL,
      blacklist: false,
      settingsOpen: false,
      fromUrl: false,
    };
  },
  methods: {
    toggleSettings() {
      this.settingsOpen = !this.settingsOpen;
    },
    shuffle(type) {
      switch (type) {
        case 'game':
          this.$store.dispatch('setRandomGame');
          break;
        case 'theme':
          this.$store.dispatch('setRandomTheme');
          break;
        case 'featured':
          this.$store.dispatch('setRandomFeaturedElements');
          break;
        case 'all':
          this.$store.dispatch('randomizeEverything');
          break;
        default:
          this.$store.dispatch('setRandomElements', type);
          break;
      }
    },
    isBlacklisted(id, type) {
      return this[`blacklisted${type}`].indexOf(id) !== -1;
    },
    toggleBlacklist(id, type) {
      if (this.isBlacklisted(id, type)) {
        this.$store.commit('removeFromBlacklist', { type, id });
      } else {
        this.$store.commit('addToBlacklist', { type, id });
      }
    },
    toggleAllBlacklist(type) {
      this.$store.dispatch('toggleAllBlacklist', type);
    },
    checkParametersAndGetElements(randomizerHash) {
      this.$store.commit('toggleInvalidState', false);
      let stateContents;
      if (randomizerHash) {
        stateContents = parseSharingCode(randomizerHash);
      }

      if (randomizerHash && !stateContents) {
        this.$store.commit('toggleInvalidState', true);
      } else {
        this.$store.dispatch('loadElements').then(() => {
          if (randomizerHash) {
            this.fromUrl = true;
            this.$store.commit('updateGeneratorSettings', stateContents);
          } else {
            this.$store.dispatch('randomizeEverything');
          }
        });
      }
    }
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.checkParametersAndGetElements(to.params.randomizerHash);
    });
  },
  beforeRouteUpdate(to, from, next) {
    this.checkParametersAndGetElements(to.params.randomizerHash);
    next();
  },
};

function getArticle(str) {
  return ((/^[aeiou]/gm).test(str.toLowerCase())) ? 'an' : 'a';
}
</script>


<style scoped>
  .loading {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-flow: column;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: #3c8ff9;
    border-radius: 6px;
    padding: 20px;
    width: 500px;
    color: #fff;
    font-family: 'Super Mario Maker Extended', sans-serif;
    text-align: center;
  }

  .loading--error {
    border: 2px solid #fff;
  }

  .idea-generator {
    width: 100%;
    max-width: 1024px;
    padding: 0 20px;
    background-color: rgba(60,143,249,.8);
  }

  .idea-generator__title {
    display: flex;
    color: #fff;
    font-size: 22px;
    font-weight: lighter;
    align-items: center;
  }

  .idea-generator__title-text {
    margin-right: auto;
  }

  .idea-generator__button {
    width: 60px;
    height: 60px;
    margin-right: 14px;
    border: 0;
    border-radius: 100%;
    background: #fff;
    color: #591d1b;
    box-shadow: 1px 5px rgba(0,0,0,.2);
    display: flex;
    text-decoration: none;
    justify-content: center;
    align-items: center;
  }

  .idea-generator__button:focus {
    outline: 0;
  }

  .idea-generator__button:active,
  .idea-generator__button--active {
    transform: translateY(2px);
    background: #fece00;
    box-shadow: 1px 2px rgba(0,0,0,.2);
  }

  .idea-generator__button .material-icons {
    font-size: 35px;
    vertical-align: middle;
  }

  .feature-group {
    display: flex;
    margin-bottom: 10px;
    padding: 0 5px 10px;
    border-radius: 6px;
    background: rgba(255, 255, 255, .2);
    flex: 1 1 100%;
    flex-flow: row wrap;
  }

  .feature-group--forbidden {
    background: #e61f10;
  }

  .feature-group__title {
    margin: 0;
    padding: 10px;
    color: #fff;
    font-size: 16px;
    font-weight: bold;
    text-align: center;
    flex: 0 0 100%;
  }

  .feature {
    margin: 0 5px 10px;
    padding: 10px;
    border: 2px solid #fff;
    border-radius: 6px;
    flex: 1;
  }

  .feature__title {
    display: flex;
    position: relative;
    margin: -12px -12px 10px;
    padding: 10px 50px 10px 10px;
    border-radius: 6px;
    background: #fffded;
    color: #591d1b;
    font-family: 'Super Mario Maker Extended', sans-serif;
    font-size: 12px;
    justify-content: flex-start;
    align-items: center;
  }

  .feature__button {
    position: absolute;
    top: 50%;
    right: 5px;
    width: 32px;
    height: 32px;
    transform: translateY(-50%);
    border: 0;
    background: transparent;
    color: #591d1b;
  }

  .feature__button:focus {
    outline: 0;
  }

  .feature__list {
    display: flex;
    margin: 0;
    padding: 0;
    list-style: none;
    flex-flow: row wrap;
    align-items: flex-start;
    justify-content: center;
  }

  .feature__item {
    display: flex;
    position: relative;
    width: 75px;
    height: 79px;
    margin: 0 5px 5px;
    border: 0;
    border-top: 8px solid #fbf600;
    border-radius: 4px;
    background: #fff;
    align-items: center;
    justify-content: flex-start;
    justify-content: center;
    align-items: center;
  }

  .feature__item--enemy {
    border-top-color: #63f40f;
  }

  .feature__item--item {
    border-top-color: #f865f7;
  }

  .feature__item--block {
    border-top-color: #00cfff;
  }

  .feature__item--gizmo {
    border-top-color: #fbf600;
  }

  .feature__item--extra {
    border-top-color: #e61f10;
  }

  .feature__item--game {
    width: 112px;
  }

  .feature__item:before {
    position: absolute;
    top: 0;
    left: 50%;
    width: 68px;
    height: 68px;
    transform: translateX(-50%);
    border: 1px solid #dccdc4;
    border-top: 0;
    border-radius: 6px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    content: '';
  }

  .feature__item--game:before {
    width: 100px;
  }

  .feature__button {
    display: flex;
    padding: 0;
    background: transparent;
    flex: 1;
    justify-content: center;
    align-items: center;
  }

  .feature__item img {
    width: 50px;
    border-radius: 4px;
    vertical-align: middle;
    image-rendering: pixelated;
  }

  .feature__name {
    display: none;
  }

  .idea-generator-settings__block-container {
    display: flex;
    flex-flow: row;
  }

  .idea-generator-settings__block-container .idea-generator-settings__block {
    flex: 1;
    margin-right: 10px;
  }

  .idea-generator-settings__block-container .idea-generator-settings__block:last-child {
    margin-right: 0;
  }

  .idea-generator-settings__block {
    margin-bottom: 10px;
    border: 2px solid #fff;
    border-radius: 6px;
  }

  .idea-generator-settings__block--with-child {
    display: flex;
  }

  .idea-generator-settings__elements {
    margin: 0;
    padding: 10px;
  }

  .idea-generator-settings__element {
    display: inline-block;
    width: 30px;
    height: 30px;
    margin-right: 5px;
    border-radius: 6px;
    background: #fff;
    text-align: center;
    overflow: hidden;
  }

  .idea-generator-settings__element img {
    width: 100%;
  }

  .idea-generator-settings__title,
  .idea-generator-settings__label {
    display: flex;
    position: relative;
    margin: -2px -2px 10px;
    padding: 10px;
    border-radius: 6px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    background: #fffded;
    color: #591d1b;
    font-family: 'Super Mario Maker Extended', sans-serif;
    font-size: 12px;
    font-weight: normal;
    justify-content: center;
  }

  .idea-generator-settings__title {
    justify-content: flex-start;
  }

  .idea-generator-settings__title-action {
    text-decoration: underline;
    text-align: center;
  }

  .idea-generator-settings__main-selection {
    flex: 3;
  }

  .idea-generator-settings__amount {
    border-left: 2px solid #fff;
    flex: 1;
    align-items: center;
  }

  .idea-generator-settings__input {
    display: block;
    width: 75px;
    height: 75px;
    margin: 0 auto 10px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: #fff;
    font-family: 'Super Mario Maker Extended', sans-serif;
    font-size: 45px;
    text-align: center;
  }

  .idea-generator-sharing {
      padding: 20px;
      text-align: center;
  }

  .idea-generator-sharing label {
      display: block;
      font-size: 14px;
      font-family: 'Super Mario Maker Extended', sans-serif;
      color: #fff;
      margin-bottom: 10px;
  }

  .idea-generator-sharing__url {
      max-width: 500px;
      width: 100%;
      height: 32px;
      border-radius: 6px;
      padding: 0 10px;
      border: 0;
      font-family: 'Super Mario Maker Extended', sans-serif;
  }


  .idea-generator-settings__input:focus,
  .idea-generator-sharing__url {
    outline: 0;
    border: 2px solid #fece00;
  }

  .blacklisted {
    opacity: .5;
  }

  .game {
    display: inline-block;
    width: 92px;
    height: 37px;
    margin-right: 10px;
    background-image: url('../assets/games.png');
    background-repeat: no-repeat;
    background-size: 185px 111px;
    flex: 0 0 92px;
  }

  .game:last-child {
    margin-right: 0;
  }

  .game--smb {
    background-position: 0 0;
  }

  .game--smb3 {
    background-position: -93px 0;
  }

  .game--smw {
    background-position: 0 -37px;
  }

  .game--nsmbu {
    background-position: -93px -37px;
  }

  .game--sm2dw {
    background-position: 0 -74px;
  }

  .error {
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-flow: column;
  }
</style>
